import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowUpRight, Menu, X, TrendingUp, Package, Wallet, ShieldCheck } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1800&q=80";


function Logo() {
  return (
    <div className="flex shrink-0 items-center gap-2" aria-label="Seller Center home">
      <img src="/logo.svg?v=2" alt="TikTok" className="h-8 w-8 shrink-0 object-contain" />
      <span className="font-head text-[22px] font-extrabold leading-none text-white">
        Seller Center
      </span>
    </div>
  );
}

function Step({ icon: Icon, n, title, text }) {
  return (
    <div className="group rounded-2xl border border-line bg-surface p-6 shadow-card transition-shadow duration-150 hover:shadow-card-hover">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
          <Icon size={19} />
        </div>
        <span className="font-head text-sm font-bold text-shop-muted">STEP {n}</span>
      </div>
      <h3 className="mt-4 font-head text-lg font-bold text-shop-ink">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-shop-body">{text}</p>
    </div>
  );
}

export default function Landing() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem("sc_entered", "1");
    } catch {}
  }, []);

  return (
    <div className="landing min-h-screen bg-base">
      {/* Announcement bar */}
      <div className="flex min-h-[57px] items-center justify-center bg-black px-4 text-center text-base text-white">
        Earn new seller incentives worth up to $23,630{" "}
        <Link to="/register" className="ml-2 inline-flex items-center gap-0.5 font-bold text-white hover:underline">
          Learn more <ArrowUpRight size={14} className="inline -mt-0.5" />
        </Link>
      </div>

      {/* Hero */}
      <section className="relative isolate h-[625px] overflow-hidden bg-black sm:h-[700px]">
        <video
          className="absolute inset-0 h-full w-full object-cover brightness-[0.4]"
          src="/hero-loop.mp4"
          poster={HERO}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
        <header className="relative z-10 flex h-16 w-full items-center justify-between px-5 sm:px-8 lg:px-16">
          <div className="flex min-w-0 items-center gap-8 xl:gap-12">
            <Logo />
          </div>

          <div className="hidden shrink-0 items-center gap-5 lg:flex">
            <span className="flex items-center gap-1 text-sm font-medium text-white">
              United States <ChevronDown size={13} />
            </span>
            <Link to="/login" className="inline-flex h-11 items-center px-1 text-sm font-medium text-white transition-colors duration-150 hover:text-brand" data-testid="nav-login">
              Log in
            </Link>
            <Link
              to="/register"
              className="inline-flex min-h-9 items-center justify-center rounded px-5 py-2 text-sm font-medium text-white transition-colors duration-150 bg-brand hover:bg-shop-primary-dark"
              data-testid="nav-join"
            >
              Join now
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="rounded p-2 text-white transition-colors duration-150 hover:bg-white/10 lg:hidden"
            aria-label={open ? "Close navigation" : "Open navigation"}
            data-testid="nav-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </header>

        {open && (
          <div className="absolute inset-x-0 top-16 z-20 border-t border-white/10 bg-black px-6 py-5 lg:hidden">
            <div className="flex flex-col gap-4">
              <Link to="/login" className="text-[15px] font-semibold text-white">Log in</Link>
              <Link to="/register" className="rounded-md bg-brand px-5 py-2.5 text-center font-semibold text-white">
                Join now
              </Link>
            </div>
          </div>
        )}

        <div className="absolute inset-x-0 top-16 z-10 flex h-[561px] flex-col items-center justify-center px-6 pb-8 text-center sm:h-[636px] sm:pb-0">
          <h1 className="max-w-[1100px] font-head text-[44px] font-extrabold leading-[1.08] text-white sm:text-[64px] lg:text-[80px]">
            Where discovery powers growth
          </h1>
          <p className="mt-12 max-w-[720px] text-lg font-medium leading-[1.4] text-white sm:text-xl">
            Reach customers through creators, shoppable video, and LIVE
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex min-h-[46px] items-center justify-center rounded bg-brand px-8 py-3 text-base font-semibold text-white transition-[background-color,transform] duration-150 hover:bg-shop-primary-dark active:scale-[0.98]"
            data-testid="hero-start-selling"
          >
            Start selling
          </Link>
        </div>

      </section>

      {/* How it works */}
      <section className="relative -mt-8 rounded-t-[32px] bg-surface">
        <div className="mx-auto max-w-[1268px] px-6 py-20 sm:px-10 lg:px-[86px] lg:py-24">
        <h2 className="max-w-[720px] font-head text-4xl font-extrabold leading-tight text-shop-ink sm:text-5xl">Start selling in four steps</h2>
        <p className="mt-4 max-w-[620px] text-base leading-relaxed text-shop-body">
          Open a store, list stock straight from the platform warehouse, and get paid as orders ship.
        </p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Step icon={ShieldCheck} n="1" title="Create your account" text="Register as a merchant and verify your identity with a quick KYC check." />
          <Step icon={Package} n="2" title="List warehouse stock" text="Pick from 500+ ready-to-ship products. No inventory to buy up front." />
          <Step icon={TrendingUp} n="3" title="Receive orders" text="Orders land in your dashboard instantly with full delivery tracking." />
          <Step icon={Wallet} n="4" title="Get paid" text="Withdraw earnings by bank transfer or USDT once an order is delivered." />
        </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black">
        <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-6 px-6 py-20 text-center">
          <h2 className="font-head text-3xl font-extrabold text-white sm:text-5xl">Ready to open your store?</h2>
          <p className="max-w-[560px] text-white/75">
            Join Seller Center today and start listing products within minutes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register"
              className="rounded-md bg-brand px-8 py-3.5 font-semibold text-white transition-colors duration-150 hover:bg-[#e01f47]"
              data-testid="cta-join"
            >
              Join now
            </Link>
            <Link
              to="/login"
              className="rounded-md border border-white/30 px-8 py-3.5 font-semibold text-white transition-colors duration-150 hover:bg-white/10"
              data-testid="cta-login"
            >
              Log in
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-line bg-surface">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-3 px-6 py-8 text-sm text-shop-muted sm:flex-row sm:items-center sm:justify-between">
          <span>© {new Date().getFullYear()} Seller Center · Training sandbox with simulated money</span>
          <div className="flex gap-6">
            <Link to="/login" className="hover:text-shop-ink">Seller login</Link>
            <Link to="/register" className="hover:text-shop-ink">Become a seller</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
